/**
 * PATH: packages/job-hub/src/jobs/tabs/QuoteIngestErrorNotice.tsx
 * FILE_ID: JOBHUB-JOBS-QUOTE-INGEST-ERROR-001
 * PURPOSE: Show why a quote import failed, and offer the one retry that fixes
 *          that failure class.
 * WHY:     A failed upload used to render as an ordinary quote row with no
 *          totals and no explanation (BUG-2026-09-03-QUOTE-INGEST-FAILS-
 *          SILENTLY), so the operator's only move was to upload the same file
 *          again into the same silence.
 * LANDMINE: the row is NOT selectable. Ticking a sentinel and pressing
 *          "Review QA plan" builds QA from a quote that has no lines.
 */
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react'
import type { QuoteIngestError } from '../jobDetailTypes'
import { retryLabel } from './quoteIngestError'

export function QuoteIngestErrorNotice({
  error,
  onRetry,
  retrying,
  retryError,
}: {
  error: QuoteIngestError
  onRetry: () => void
  retrying: boolean
  retryError?: string | null
}) {
  return (
    <div
      role="alert"
      className="mt-1 rounded-lg border border-destructive/40 bg-destructive/5 px-2.5 py-2"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-medium text-destructive">
            This quote could not be imported
          </p>
          {error.hint && <p className="mt-0.5 text-[11px] text-foreground/80">{error.hint}</p>}
          {error.reason && (
            <p className="mt-0.5 break-words text-[11px] text-muted-foreground">{error.reason}</p>
          )}
          {retryError && (
            <p className="mt-1 text-[11px] font-medium text-destructive">{retryError}</p>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onRetry()
          }}
          disabled={retrying}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-destructive/40 px-2 py-1 text-[11px] font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
        >
          {retrying ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-3 w-3" aria-hidden />
          )}
          {retryLabel(error.code)}
        </button>
      </div>
    </div>
  )
}
